import { UserConsumer } from '../../core/providers/UserProvider';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { APPLICATION_PATHS } from '../../application-core/constants';
import ActionText from '../../core/ActionText';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import MenuSelector from '../../core/MenuSelector';
import Paging from '../../core/Paging';
import { globalStyles } from '../../core/style/gstheme';
import { STATUS_ACCEPTED, STATUS_PENDING, STATUS_REJECTED } from '../../core/utils/constants';
import { makeCancelable, sortByProp } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import { SUGGESTION_TYPE_OPTIONS } from '../constants';
import Suggestion from './Suggestion';

const styles = () => {
    return Object.assign({
        options: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        }
    }, globalStyles);
};

const typeIRIs = [
    "https://grafsync.graphifi.com/schema/vm/NewConceptSuggestion",
    "https://grafsync.graphifi.com/schema/vm/SplitSuggestion",
    "https://grafsync.graphifi.com/schema/vm/RemovalSuggestion",
    "https://grafsync.graphifi.com/schema/vm/DeprecationSuggestion",
    "https://grafsync.graphifi.com/schema/vm/MovingSuggestion",
    "https://grafsync.graphifi.com/schema/vm/SpellingSuggestion",
    "https://grafsync.graphifi.com/schema/vm/TerminologySuggestion"
];

class Suggestions extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            page: 1,
            suggestionType: "All",
            status: "All",
            suggestions: null,
            pageCount: null,
            assignee: "All",
            user: "All",
            users: [],
            sortedUsers: [],
            isLoadingSuggestions: true,
            isLoadingUsers: true,
            isErrored: null,
            allowNotifications: GS.getActiveSubscriptionType() === "Managed" || GS.getActiveSubscriptionType() === "Trial" || GS.getActiveSubscriptionType() === "Premium"
        };
        this.cancellable1 = null;
        this.cancellable2 = null;
    }

    componentDidMount() {
        this.getSuggestions();
        this.getUsers();
    }

    componentWillUnmount() {
        if (this.cancellable1) this.cancellable1.cancel();
        if (this.cancellable2) this.cancellable2.cancel();
    }

    getUsers = async () => {
        this.setState(
            { isLoadingUsers: true },
            () => {
                this.doGetUsers()
                    .promise
                    .then(data => {
                        if (this.cancellable2 && this.cancellable2.isCancelled()) return;
                        if (data) {
                            const sortedUsers = data.map(user => ({
                                value: user.id,
                                label: user.givenName && user.familyName ? user.givenName + " " + user.familyName : user.username
                            }))
                                .sort(function (a, b) {
                                    return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1;
                                });
                            this.setState({ users: data, sortedUsers, isLoadingUsers: false });
                        }
                        else {
                            this.setState({ isLoading: false });
                        }
                    })
                    .catch((e) => {
                        if (this.cancellable2 && !this.cancellable2.isCancelled()) this.setState({ isErrored: e });
                    });
            });
    };

    getSuggestions = () => {
        this.setState({ isChangingSuggestions: true }, () => {
            const { suggestionType, status, page, user, assignee } = this.state;
            const suggestionValue = suggestionType && suggestionType !== "All" ?
                ["https://grafsync.graphifi.com/schema/vm/" + suggestionType]
                :
                typeIRIs;
            let search = [
                {
                    predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    value: suggestionValue
                },
                {
                    predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                    value: this.props.schemeIRI
                },
                {
                    predicate: "https://grafsync.graphifi.com/schema/vm/suggestionSourceConcept"
                }
            ];
            if (status !== "All") {
                search.push({
                    predicate: "https://grafsync.graphifi.com/schema/gs/status",
                    value: status
                });
            }
            if (user !== "All") {
                search.push({
                    predicate: "https://graphifi.com/schema/accesscontrol/user",
                    value: user
                });
            }
            if (assignee !== "All") {
                search.push({
                    predicate: "https://grafsync.graphifi.com/schema/gs/assignedUser",
                    value: assignee
                });
            }
            this
                .doSearch({
                    search,
                    graph: [this.props.graph],
                    page,
                    pageSize: 3,
                    sort: ["http://purl.org/dc/terms/created"]
                })
                .promise
                .then(data => {
                    if (this.cancellable1 && this.cancellable1.isCancelled()) return;
                    const suggestions = data.results && data.results.length > 0 ? sortByProp([...data.results], 'created') : null;
                    const pageCount = data.pageCount;
                    const page = data.page;
                    this.setState({ suggestions, isLoadingSuggestions: false, isChangingSuggestions: false, page, pageCount });
                })
                .catch((e) => {
                    if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
                });
        });
    };

    doGetUsers() {
        this.cancellable2 = makeCancelable(GS.subscriptionUsers());
        return this.cancellable2;
    }

    doSearch(payload) {
        this.cancellable1 = makeCancelable(GS.search(payload));
        return this.cancellable1;
    }

    handlePageChange = (page) => {
        this.setState({ page }, () => this.getSuggestions());
    };

    handleTypeChange = (suggestionType) => {
        this.setState({ page: 1, suggestionType }, () => this.getSuggestions());
    };

    handleStatusChange = (status) => {
        this.setState({ page: 1, status }, () => this.getSuggestions());
    };

    handleUserChange = (user) => {
        this.setState({ page: 1, user }, () => this.getSuggestions());
    };

    handleAssigneeChange = (assignee) => {
        this.setState({ page: 1, assignee }, () => this.getSuggestions());
    };

    onSelect(link) {
        this.props.history.push(link);
        this.props.onToggleView();
    }

    computeLink(conceptIRI) {
        const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' + this.props.projectIRI + "&taxonomy="
            + this.props.schemeIRI + "&view=concept&concept=" + conceptIRI;
        return link;
    }

    render() {

        const { classes, graph, defaultLang, schemeIRI, components, projectIRI, onToggleView } = this.props;
        const { suggestions, suggestionType, page, status, user, pageCount, isChangingSuggestions, allowNotifications,
            isErrored, isLoadingUsers, isLoadingSuggestions, users, sortedUsers, assignee } = this.state;

        if (isErrored) return <Errored data={isErrored} />;
        if (isLoadingUsers || isLoadingSuggestions) return <Loading minHeight="140px" offset="100px" />;
        if ((!suggestions || (suggestions && suggestions.length === 0)) && (user === "All" && assignee === "All" && status === "All" && suggestionType === "All")) return (
            <div style={{ marginBottom: 120 }}>
                <ActionText offset="120px">There are no suggestions.</ActionText>
            </div>
        );

        return (
            <UserConsumer>
                {({ user }) =>
                    <div data-test="suggestions-view">
                        <Grid container spacing={2} classes={
                            {
                                "spacing-xs-2": this.props.classes.fullGrid
                            }
                        }>
                            <Grid item xs={12}>
                                <div data-test="suggestion-filtering" className={classes.filterArea}>
                                    <Grid container spacing={2} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        <Grid item xs={3}>
                                            <MenuSelector
                                                dataTest="type-selector"
                                                label="Suggestion Type"
                                                selected="All"
                                                handleChange={this.handleTypeChange}
                                                hintWord="suggestion type"
                                                options={[
                                                    {
                                                        "value": "All",
                                                        "label": "All"
                                                    },
                                                    ...SUGGESTION_TYPE_OPTIONS
                                                ]} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <MenuSelector
                                                dataTest="user-selector"
                                                label="Suggestion by User"
                                                selected="All"
                                                handleChange={this.handleUserChange}
                                                hintWord="user"
                                                options={[
                                                    {
                                                        "value": "All",
                                                        "label": "All"
                                                    },
                                                    ...sortedUsers
                                                ]} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <MenuSelector
                                                dataTest="assignee-selector"
                                                disabled={!allowNotifications}
                                                label="Suggestion by Assignee"
                                                selected="All"
                                                handleChange={this.handleAssigneeChange}
                                                hintWord="user"
                                                options={[
                                                    {
                                                        "value": "All",
                                                        "label": "All"
                                                    },
                                                    ...sortedUsers
                                                ]} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <MenuSelector
                                                dataTest="status-selector"
                                                label="Suggestion Status"
                                                selected="All"
                                                handleChange={this.handleStatusChange}
                                                hintWord=" suggestion status"
                                                options={[
                                                    {
                                                        "value": "All",
                                                        "label": "All"
                                                    },
                                                    {
                                                        "value": STATUS_PENDING,
                                                        "label": "Open"
                                                    },
                                                    {
                                                        "value": STATUS_ACCEPTED,
                                                        "label": "Accepted"
                                                    },
                                                    {
                                                        "value": STATUS_REJECTED,
                                                        "label": "Rejected"
                                                    }
                                                ]} />
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                            {suggestions && suggestions.map(suggestion =>
                                <Grid data-test="suggestion-item" key={suggestion.id} item xs={4}>
                                    <Suggestion
                                        fullHeight={true}
                                        schemeIRI={schemeIRI}
                                        showDiscussion={false}
                                        defaultLang={defaultLang}
                                        components={components}
                                        projectIRI={projectIRI}
                                        showSource={true}
                                        key={suggestion.id}
                                        user={user}
                                        id={suggestion.id}
                                        graph={graph}
                                        onToggleView={onToggleView}
                                        updateEvent={this.monitorSuggestionUpdate}
                                        isReadOnly={true}
                                        users={users} />
                                </Grid>
                            )}
                            {(pageCount > 1 || !suggestions || suggestions.length === 0) &&
                                <Grid item xs={12}>
                                    {pageCount > 1 &&
                                        <div data-test="paging-section" style={{ marginTop: '20px', height: 40 }}>
                                            {!isChangingSuggestions &&
                                                <Paging page={page} pageCount={pageCount} pageItemsRange={5} handlePageChange={this.handlePageChange} />
                                            }
                                        </div>
                                    }
                                    {(!suggestions || suggestions.length === 0) &&
                                        <div style={{ marginBottom: 140, width: '100%' }}>
                                            <ActionText offset="140px">No results. Please adjust the filters.</ActionText>
                                        </div>
                                    }
                                </Grid>
                            }
                        </Grid>
                    </div>
                }
            </UserConsumer>
        );
    }
}

Suggestions.defaultProps = {
    useXL: false
};

Suggestions.propTypes = {
    id: PropTypes.string.isRequired,
    history: PropTypes.object,
    projectIRI: PropTypes.string.isRequired,
    schemeIRI: PropTypes.string.isRequired,
    useXL: PropTypes.bool,
    components: PropTypes.array.isRequired,
    defaultLang: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    onToggleView: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withRouter(withStyles(styles)(Suggestions));